// LibraryHome (SuiteModel/LibraryHome.swift): what the Library's home page says — the greeting, the week in a
// sentence, the book's parts against its target, the last sentence before the caret, and how long a project has rested.
// Days are this runtime's (the writer's) local days, as the word log's `YYYY-MM-DD` keys are.
import type { DailyWords } from '../contract.ts';
import { sentences } from '../history/textDiff.ts';
import { JournalPosts } from '../edit/editProject.ts';
import { markdownBefore, plainText } from '../proseMarkdown.ts';

/** "Good morning, Ada." by the hour; the first name only, or none. */
export function greeting(name: string | null | undefined, at: Date = new Date()): string {
  const hour = at.getHours();
  const part = hour >= 5 && hour < 12 ? 'Good morning' : hour >= 12 && hour < 18 ? 'Good afternoon' : 'Good evening';
  const first = (name ?? '').split(' ').filter(Boolean)[0] ?? '';
  return first ? `${part}, ${first}.` : `${part}.`;
}

export interface Day {
  /** Local midnight of the day. */
  date: Date;
  /** `YYYY-MM-DD` (the word log's key). */
  day: string;
  words: number;
}

export interface Week {
  /** The last seven days, oldest first; the last is today. */
  days: Day[];
  sentence: string;
}

/** `WritingDay.key` in this runtime's zone. */
export function dayKey(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** Words added per day, every device's log merged (`WritingHistory.words(on:)`). */
export function addedByDay(daily: DailyWords[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const d of daily) out.set(d.day, (out.get(d.day) ?? 0) + d.added);
  return out;
}

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven'];

/** "no" … "seven". */
export function numberWord(n: number): string {
  return NUMBER_WORDS[Math.max(0, Math.min(7, n))];
}

/** The last seven days' written words, and how many of them had writing. */
export function week(daily: DailyWords[], today: Date = new Date(), locale = 'en-GB'): Week {
  const byDay = addedByDay(daily);
  const start = startOfDay(today);
  const days: Day[] = [6, 5, 4, 3, 2, 1, 0].map((back) => {
    const date = addDays(start, -back);
    const day = dayKey(date);
    return { date, day, words: byDay.get(day) ?? 0 };
  });
  const writing = days.filter((d) => d.words > 0);
  let sentence =
    writing.length === 0
      ? 'Nothing written in the last seven days.'
      : writing.length === 7
        ? "You've written every day this week."
        : `You've written on ${numberWord(writing.length)} of the last seven days.`;
  if (writing.length >= 2) {
    // `max(by:)`: the first of equals.
    const best = writing.reduce((a, b) => (b.words > a.words ? b : a));
    const name = best.day === dayKey(start) ? 'today' : new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(best.date);
    sentence += ` The most was ${name}: ${new Intl.NumberFormat(locale).format(best.words)} words.`;
  }
  return { days, sentence };
}

export interface Part {
  title: string;
  words: number;
  /** Its share of the bar: words over the target (or the whole book when it's past the target). */
  fraction: number;
}

/** The book's parts (or chapters) as shares of one bar measured against the manuscript target. */
export function parts(list: { title: string; words: number }[], target: number | null | undefined): Part[] {
  const total = list.reduce((n, p) => n + Math.max(0, p.words), 0);
  const scale = Math.max(total, target ?? 0, 1);
  return list.map((p) => ({ title: p.title, words: Math.max(0, p.words), fraction: Math.max(0, p.words) / scale }));
}

/** A journal's post for its Home card (`LibraryHome.Journal.Post`). */
export interface JournalPost {
  id: string;
  title: string;
  /** The status option's id. */
  status: string | null;
  date: Date | null;
}

/** A journal's card in place of the parts bar (`LibraryHome.Journal`): its posts by status, the last one out, the next to go. */
export interface JournalSummary {
  posts: number;
  /** How many posts have each status, in the status field's order, then the rest (`status` null: none, or one the field
   *  no longer has); statuses no post has are left out. */
  counts: { status: string | null; count: number }[];
  /** The published post with the latest publish date (an undated one only when none is dated). */
  lastPublished: JournalPost | null;
  /** The scheduled post with the earliest publish date, else the draft nearest the top of the binder (where new posts go). */
  next: JournalPost | null;
}

/** `LibraryHome.journal`: `posts` in the binder's order (newest at the top); `statuses` the status field's option ids in
 *  workflow order. Among equal dates (or none) the post nearest the top wins, as Swift's `max`/`min` keep the first. */
export function journalSummary(posts: JournalPost[], statuses: string[]): JournalSummary {
  const counts: JournalSummary['counts'] = [];
  for (const id of statuses) {
    const n = posts.filter((p) => p.status === id).length;
    if (n > 0) counts.push({ status: id, count: n });
  }
  const other = posts.filter((p) => p.status === null || !statuses.includes(p.status)).length;
  if (other > 0) counts.push({ status: null, count: other });
  const time = (p: JournalPost, missing: number) => p.date?.getTime() ?? missing;
  let last: JournalPost | null = null;
  for (const p of posts) if (p.status === JournalPosts.published && (!last || time(p, -Infinity) > time(last, -Infinity))) last = p;
  let soonest: JournalPost | null = null;
  for (const p of posts) if (p.status === JournalPosts.scheduled && (!soonest || time(p, Infinity) < time(soonest, Infinity))) soonest = p;
  return {
    posts: posts.length, counts, lastPublished: last,
    next: soonest ?? posts.find((p) => p.status === JournalPosts.draft) ?? null,
  };
}

const FINISHING = new Set(['.', '!', '?', '…', '”', '’', '"', "'"]);

/**
 * The last sentence written before `offset` (in Unicode scalars) in stored Markdown: the one the caret is in if it
 * has a few words already (ending "…"), else the one before; null at the start.
 */
export function lastSentence(text: string, offset: number): string | null {
  // Cut where the caret is, closing a span it's inside so its brackets don't show.
  let prefix = markdownBefore(text, offset);
  // Only the last few paragraphs matter, and plain text is simpler to split.
  const paragraphs = prefix.split('\n\n').filter((p) => p.trim().length > 0);
  prefix = paragraphs.slice(-3).join('\n\n');
  const plain = plainText(prefix)
    .replace(/\s+/g, ' ')
    .replace(/^ +| +$/g, '');
  if (!plain) return null;
  const all = sentences(plain)
    .map((s) => s.trim())
    .filter(Boolean);
  const last = all.at(-1);
  if (!last) return null;
  const end = Array.from(last).at(-1) ?? '';
  if (FINISHING.has(end)) return last;
  if (last.split(' ').filter(Boolean).length >= 4) return `${last}…`;
  return all.length > 1 ? all[all.length - 2] : `${last}…`;
}

/** Whole days from one date to another, as `Calendar.dateComponents([.day])` counts them (local days). */
function daysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  let days = Math.round((b - a) / 86_400_000);
  // A partial day doesn't count: 10 pm to 9 am the next day is 0 days.
  const fromTime = from.getHours() * 3600 + from.getMinutes() * 60 + from.getSeconds();
  const toTime = to.getHours() * 3600 + to.getMinutes() * 60 + to.getSeconds();
  if (days > 0 && toTime < fromTime) days -= 1;
  return days;
}

/** For a project left alone a while: "untouched since June. It'll keep." (null if touched in the last six weeks). */
export function untouched(since: Date, now: Date = new Date(), locale = 'en-GB'): string | null {
  if (Number.isNaN(since.getTime()) || daysBetween(since, now) <= 42) return null;
  const sameYear = since.getFullYear() === now.getFullYear();
  const when = new Intl.DateTimeFormat(locale, sameYear ? { month: 'long' } : { month: 'long', year: 'numeric' }).format(since);
  return `untouched since ${when}. It'll keep.`;
}
